import React, {useEffect, useRef, useState} from 'react'
import {uploadTransportNalogImageSingle} from '../../api/fmkNalozi'
import notifyToast from '../../components/Toast/Toast'

/* ================= TYPES ================= */

type UploadedImage = {
    Id: number
    url?: string
    path?: string
}

type UploadStatus =
    | 'queued'
    | 'uploading'
    | 'done'
    | 'error'
    | 'canceled'

type UploadItem = {
    key: string
    file: File
    previewUrl: string
    progress: number
    status: UploadStatus
    uploaded?: UploadedImage
    controller?: AbortController
}

type Props = {
    nalogSafe: string
    onUploadedChange?: (images: UploadedImage[]) => void
    onUploadingChange?: (uploading: boolean) => void
}

/* ================= CONFIG ================= */

const MAX_PARALLEL_UPLOADS = 2

/* ================= COMPONENT ================= */

const ImageUploader: React.FC<Props> = ({
                                            nalogSafe,
                                            onUploadedChange,
                                            onUploadingChange
                                        }) => {

    const [items, setItems] = useState<UploadItem[]>([])

    const queueRef = useRef<UploadItem[]>([])
    const runningRef = useRef(0)

    /* ===============================
       SAFE PARENT NOTIFICATION
    =============================== */

    useEffect(() => {

        const uploading =
            items.some(
                i =>
                    i.status === 'queued' ||
                    i.status === 'uploading'
            )

        onUploadingChange?.(uploading)

        onUploadedChange?.(
            items
                .filter(i => i.status === 'done' && i.uploaded)
                .map(i => i.uploaded!)
        )

    }, [items])

    /* ===============================
       QUEUE SYSTEM
    =============================== */

    const pushQueue = (newItems: UploadItem[]) => {
        queueRef.current.push(...newItems)
        drainQueue()
    }

    const drainQueue = () => {

        while (
            runningRef.current < MAX_PARALLEL_UPLOADS &&
            queueRef.current.length
            ) {
            const item = queueRef.current.shift()
            if (item) startUpload(item)
        }
    }

    /* ===============================
       UPLOAD
    =============================== */

    const startUpload = async (item: UploadItem) => {

        runningRef.current++

        const controller = new AbortController()

        setItems(prev =>
            prev.map(i =>
                i.key === item.key
                    ? {...i, status:'uploading', controller}
                    : i
            )
        )

        try {

            const uploaded =
                await uploadTransportNalogImageSingle(
                    nalogSafe,
                    item.file,
                    pct =>
                        setItems(prev =>
                            prev.map(i =>
                                i.key === item.key
                                    ? {...i, progress:pct}
                                    : i
                            )
                        ),
                    controller.signal
                )

            setItems(prev =>
                prev.map(i =>
                    i.key === item.key
                        ? {
                            ...i,
                            status:'done',
                            progress:100,
                            uploaded
                        }
                        : i
                )
            )

        } catch (e:any) {

            const canceled =
                e?.name === 'CanceledError' ||
                e?.code === 'ERR_CANCELED'

            setItems(prev =>
                prev.map(i =>
                    i.key === item.key
                        ? {
                            ...i,
                            status: canceled
                                ? 'canceled'
                                : 'error'
                        }
                        : i
                )
            )

            if (!canceled)
                notifyToast(false, e.message)

        } finally {

            runningRef.current--
            drainQueue()
        }
    }

    /* ===============================
       FILE SELECT
    =============================== */

    const onChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {

        if (!e.target.files) return

        const newItems: UploadItem[] =
            Array.from(e.target.files).map(file => ({
                key: crypto.randomUUID(),
                file,
                previewUrl: URL.createObjectURL(file),
                progress: 0,
                status: 'queued'
            }))

        setItems(prev => [...prev, ...newItems])

        // queue AFTER render commit
        setTimeout(() => pushQueue(newItems), 0)

        e.target.value = ''
    }

    /* ===============================
       CANCEL
    =============================== */

    const cancelUpload = (key:string) => {

        setItems(prev =>
            prev.map(i => {
                if (i.key === key) {
                    i.controller?.abort()
                    return {...i, status:'canceled'}
                }
                return i
            })
        )
    }

    /* ===============================
       UI
    =============================== */

    return (
        <div className='mt-4'>

            <h5>📸 Fotografije utovara</h5>

            <input
                type='file'
                multiple
                accept='image/*'
                capture='environment'
                className='form-control mb-3'
                onChange={onChange}
            />

            <div className='row'>
                {items.map(it => (

                    <div
                        key={it.key}
                        className='col-4 mb-3'
                    >

                        <img
                            src={it.previewUrl}
                            className='img-fluid rounded'
                            alt=''
                        />

                        <div className='progress mt-2'>
                            <div
                                className={`progress-bar ${
                                    it.status === 'error'
                                        ? 'bg-danger'
                                        : it.status === 'done'
                                            ? 'bg-success'
                                            : 'progress-bar-striped progress-bar-animated'
                                }`}
                                style={{
                                    width:`${it.progress}%`,
                                    transition:'width .2s linear'
                                }}
                            />
                        </div>

                        {it.status === 'uploading' && (
                            <button
                                className='btn btn-sm btn-light w-100 mt-1'
                                onClick={()=>cancelUpload(it.key)}
                            >
                                Cancel
                            </button>
                        )}

                    </div>
                ))}
            </div>
        </div>
    )
}

export default ImageUploader